import {Edit} from "@mui/icons-material";
import {TabContext, TabPanel} from "@mui/lab";
import {Tab, Tabs} from "@mui/material";
import {useQuery} from "@tanstack/react-query";
import React, {useEffect, useRef} from 'react';
import {StereotypeApiClient} from "../../../../common/apis/stereotype/StereotypeApiClient";
import {ArchimateElement} from "../../../../common/archimate/ArchimateElement";
import Dialog from '../../../../components/dialogs/Dialog';
import DialogContent from '../../../../components/dialogs/DialogContent';
import DialogTitle from '../../../../components/dialogs/DialogTitle';
import Grid from '../../../../components/dialogs/Grid';
import TextField from '../../../../components/fields/textfield/TextField';
import SectionActionBar from "../../../../components/section/SectionActionBar";
import {SectionActionButton} from "../../../../components/section/SectionActionButton";
import SectionHeader from "../../../../components/section/SectionHeader";
import {t} from '../../../../store/localization/TranslMessasge';
import {CommonTranslation} from '../CommonTranslation';
import {ErrorTranslationKey} from "../ErrorTranslationKey";
import Snackbar from "../snackbar/Snackbar";
import AttributeDefinitionGrid from "./attributes/AttributeDefinitionGrid";
import LayoutDefinitionPanel from './layout/LayoutDefinitionPanel';
import ReferenceDefinitionGrid from "./reference/ReferenceDefinitionGrid";
import {StereotypesTranslationKey} from "./StereotypesTranslationKey";
import {StereotypeUpdateDialog} from "./StereotypeUpdateDialog";

enum StereotypeDetailTab {
    ATTRIBUTES = "attributes",
    SMART_REFERENCES = "smartReferences",
    LAYOUT = "layout"
}

interface StereotypeDetailDialogProps {
    open: boolean;
    stereotypeId: string;
    onClose: (updated: boolean) => void;
}

export function StereotypeDetailDialog({ open, stereotypeId, onClose }: Readonly<StereotypeDetailDialogProps>) {
    const stereotypeApiClient = useRef(new StereotypeApiClient());

    const [selectedTab, setSelectedTab] = React.useState<StereotypeDetailTab>(StereotypeDetailTab.ATTRIBUTES);
    const [stereotypeUpdateDialogOpen, setStereotypeUpdateDialogOpen] = React.useState(false);
    const [updated, setUpdated] = React.useState(false);

    const {data: stereotype, isError, error, refetch} = useQuery({
        queryKey: ["stereotypes.stereotype", stereotypeId],
        queryFn: () => stereotypeApiClient.current.findById(stereotypeId),
    });

    useEffect(() => {
        if (isError && error) {
            Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error);
        }
    }, [isError, error]);

    useEffect(() => {
        if (open) {
            setUpdated(false);
        }
    }, [open]);

    function onUpdated() {
        setUpdated(true);
        refetch();
    }

    function openStereotypeUpdateDialog() {
        setStereotypeUpdateDialogOpen(true);
    }

    function closeStereotypeUpdateDialog() {
        setStereotypeUpdateDialogOpen(false)
    }

    function onDialogClose() {
        onClose(updated);
    }

    if (!stereotype) {
        return null;
    }

    function formatElementType(elementType: string): string {
        return ArchimateElement.findByStandardName(elementType)?.visibleName ?? "";
    }

    return (
            <>
                <Dialog open={open} onClose={onDialogClose} maxWidth="md">
                    <DialogTitle title={t(StereotypesTranslationKey.STEREOTYPE)}
                            onDialogClosed={onDialogClose} />
                    <DialogContent>
                        <Grid container>
                            <Grid item xs={12}>
                                <SectionHeader title={t(StereotypesTranslationKey.DETAILS)}>
                                    <SectionActionBar>
                                        <SectionActionButton
                                                icon={<Edit fontSize={"inherit"}/>}
                                                tooltip={t(CommonTranslation.EDIT)}
                                                onClick={openStereotypeUpdateDialog}
                                                visible={true}/>
                                    </SectionActionBar>
                                </SectionHeader>
                            </Grid>
                            <Grid item xs={12} sm={7}>
                                <TextField
                                        label={t(StereotypesTranslationKey.NAME)}
                                        value={stereotype.name}
                                        readOnly={true}
                                        fullWidth/>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                        label={t(StereotypesTranslationKey.BASE_TYPE)}
                                        value={formatElementType(stereotype.elementType)}
                                        readOnly={true}
                                        fullWidth/>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                        label={t(CommonTranslation.DESCRIPTION)}
                                        value={stereotype.description ?? ''}
                                        readOnly={true}
                                        multiline
                                        rows={4}
                                        fullWidth/>
                            </Grid>
                            <Grid item xs={12}>
                                <TabContext value={selectedTab}>
                                    <Tabs value={selectedTab} onChange={(event, newValue) => setSelectedTab(newValue as StereotypeDetailTab)}>
                                        <Tab value={StereotypeDetailTab.ATTRIBUTES} label={t(StereotypesTranslationKey.ATTRIBUTES)} />
                                        <Tab value={StereotypeDetailTab.SMART_REFERENCES} label={t(StereotypesTranslationKey.SMART_REFERENCES)} />
                                        <Tab value={StereotypeDetailTab.LAYOUT} label={t(StereotypesTranslationKey.LAYOUT)} />
                                    </Tabs>
                                    <TabPanel value={StereotypeDetailTab.ATTRIBUTES}>
                                        <AttributeDefinitionGrid
                                                stereotype={stereotype}
                                                onUpdated={onUpdated}/>
                                    </TabPanel>
                                    <TabPanel value={StereotypeDetailTab.SMART_REFERENCES}>
                                        <ReferenceDefinitionGrid
                                                stereotype={stereotype}
                                                onUpdated={onUpdated}/>
                                    </TabPanel>
                                    <TabPanel value={StereotypeDetailTab.LAYOUT} sx={{paddingBottom: 0}}>
                                        <LayoutDefinitionPanel
                                                stereotype={stereotype}
                                                onUpdated={onUpdated}/>
                                    </TabPanel>
                                </TabContext>
                            </Grid>

                        </Grid>
                    </DialogContent>
                </Dialog>

                <StereotypeUpdateDialog open={stereotypeUpdateDialogOpen}
                        stereotype={stereotype}
                        onClose={closeStereotypeUpdateDialog}
                        onUpdated={onUpdated} />
            </>
    );
}